import { CheckIcon, SearchIcon, XIcon } from "lucide-react";
import { useState } from "react";
import { Badge } from "../../../components/ui/badge";
import { FormControl, FormItem, FormLabel, FormMessage } from "../../../components/ui/form";
import { Input } from "../../../components/ui/input";
import { RadioGroup, RadioGroupItem } from "../../../components/ui/radio-group";
import { ScrollArea } from "../../../components/ui/scroll-area";
import { Skeleton } from "../../../components/ui/skeleton";

export interface EntitySelectorProps<T> {
  name: string;
  label: string;
  placeholder: string;
  data: T[];
  isLoading: boolean;
  value: number | undefined;
  onChange: (value: number) => void;
  hasError?: boolean;
  errorMessage?: string;
  renderLabel: (item: T) => React.ReactNode;
  renderSubLabel?: (item: T) => React.ReactNode | null;
  getItemId: (item: T) => number;
  getItemName: (item: T) => string;
  onSearch?: (term: string) => void;
  searchTerm?: string;
  serverSideSearch?: boolean;
  totalItems?: number;
}

export function EntitySelector<T>({
  name,
  label,
  placeholder,
  data,
  isLoading,
  value,
  onChange,
  hasError,
  errorMessage,
  renderLabel,
  renderSubLabel,
  getItemId,
  getItemName,
  onSearch,
  searchTerm: externalSearchTerm,
  serverSideSearch = false,
  totalItems,
}: EntitySelectorProps<T>) {
  const [internalSearchTerm, setInternalSearchTerm] = useState("");

  // Use either the external search term (for server-side) or internal (for client-side)
  const searchTerm = serverSideSearch ? externalSearchTerm || "" : internalSearchTerm;

  // Handle search term changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTerm = e.target.value;
    if (serverSideSearch && onSearch) {
      onSearch(newTerm);
    } else {
      setInternalSearchTerm(newTerm);
    }
  };

  // Clear search term
  const clearSearch = () => {
    if (serverSideSearch && onSearch) {
      onSearch("");
    } else {
      setInternalSearchTerm("");
    }
  };

  // Filter data client-side if not using server-side filtering
  const filteredData = !serverSideSearch && searchTerm
    ? data.filter(item =>
      getItemName(item).toLowerCase().includes(searchTerm.toLowerCase())
    )
    : data;

  // Determine the count to display
  const displayCount = serverSideSearch ? totalItems || filteredData.length : filteredData.length;

  return (
    <FormItem className="space-y-4">
      <FormLabel className="text-base font-medium flex items-center gap-2">
        <Badge variant="outline" className="py-1">{label}</Badge>
        {`Sélectionner un ${label.toLowerCase()}`}
      </FormLabel>
      <FormControl>
        <div className={`border rounded-md p-4 ${hasError ? "border-red-500" : ""}`}>
          <div className="mb-4 space-y-2">
            <div className="relative">
              <Input
                placeholder={placeholder}
                value={searchTerm}
                onChange={handleSearchChange}
                className="pl-9"
              />
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <SearchIcon className="h-4 w-4 text-gray-400" />
              </div>
              {searchTerm && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute inset-y-0 right-0 flex items-center pr-3"
                >
                  <XIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
            <div className="text-xs text-gray-500 flex justify-between">
              <span>
                {displayCount} {label.toLowerCase()}
                {displayCount !== 1 ? 's' : ''} trouvé
                {displayCount !== 1 ? 's' : ''}
                {serverSideSearch && searchTerm ? " correspondant à la recherche" : ""}
              </span>
              {searchTerm && <span>Recherche: "{searchTerm}"</span>}
            </div>
          </div>
          <ScrollArea className="h-[250px] pr-4">
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : (
              <RadioGroup
                onValueChange={(val) => onChange(Number(val))}
                defaultValue={value ? String(value) : undefined}
                className="flex flex-col space-y-2"
              >
                {filteredData.length > 0 ? (
                  filteredData.map((item) => {
                    const id = getItemId(item);
                    return (
                      <div
                        key={id}
                        className={`flex items-center space-x-3 rounded-md border p-3 transition-colors cursor-pointer ${value === id
                          ? 'bg-blue-50 border-blue-200 shadow-sm'
                          : 'hover:bg-slate-50'
                          }`}
                        onClick={() => onChange(id)}
                      >
                        <RadioGroupItem value={String(id)} id={`${name}-${id}`} />
                        <label
                          htmlFor={`${name}-${id}`}
                          className={`flex-grow text-sm font-medium cursor-pointer flex items-center justify-between ${value === id ? 'text-blue-700' : ''
                            }`}
                        >
                          <div className="flex flex-col">
                            {renderLabel(item)}
                            {renderSubLabel && renderSubLabel(item)}
                          </div>
                          {value === id && (
                            <CheckIcon className="h-5 w-5 text-blue-600 ml-2 flex-shrink-0" />
                          )}
                        </label>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-4 text-sm text-gray-500">
                    {searchTerm
                      ? `Aucun ${label.toLowerCase()} ne correspond à votre recherche`
                      : `Aucun ${label.toLowerCase()} disponible`}
                  </div>
                )}
              </RadioGroup>
            )}
          </ScrollArea>
        </div>
      </FormControl>
      <FormMessage />
      {hasError && errorMessage && (
        <p className="text-sm font-medium text-red-500 mt-1">{errorMessage}</p>
      )}
    </FormItem>
  );
}
